import "./ProjectAsset.scss"
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { memo, useMemo } from "react"
import AssetItem from "./AssetItem"
import BdaApplicationItem from "./BdaApplicationItem"
import ServiceItem from "./ServiceItem"
import { BdaApplication } from "../../models/BdaApplication"
import { BdaDataset } from "../../models/BdaDataset"
import { BdaService } from "../../models/BdaService"
import { BdaModel } from "../../models/BdaModel"
import { setObjectType } from "../../utils"
import { useSelector } from "react-redux"
import { RootState } from "../../store"
import { sectionLabelsSelector } from "../../slices/sectionsSlice"

const assetIcons = {
  application: solid('laptop-code'),
  dataset: solid('database'),
  model: solid('graduation-cap'),
  service: solid('cubes'),
}

type Props = {
  asset: BdaApplication | BdaDataset | BdaModel | BdaService;
  type: 'application' | 'dataset' | 'model' | 'service';
  actions?: React.ReactNode;
}

export const ProjectAsset = memo(({ asset, type, actions }: Props) => {
  const applicationLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "applications"));
  const serviceLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "services"));
  const datasetLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "datasets"));
  const modelLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "models"));

  const assetLabels: Record<"application" | "dataset" | "model" | "service", string> = useMemo(() => ({
    service: serviceLabels.singular || "Service",
    model: modelLabels.singular || "Model",
    dataset: datasetLabels.singular || "Dataset",
    application: applicationLabels.singular || "Application",
  }), [applicationLabels, serviceLabels, datasetLabels, modelLabels]);

  const onClick = () => {
    window.open(`${window.location.origin}/${type === "application" ? "bda-application" : type}s/${asset.id}`, "_blank");
  };

  return (
    <div className={`project-asset ${type}`} onClick={onClick}>
      <h6 className="asset-type">
        <FontAwesomeIcon icon={assetIcons[type]} fixedWidth />
        <span className="ms-2">{assetLabels[type]}</span>
      </h6>

      {type === "application" && setObjectType<BdaApplication>(asset) && (
        <BdaApplicationItem
          projectMode={true}
          application={asset}
          actions={actions || <></>}
        />
      )}

      {(type === "dataset" || type === "model") && setObjectType<BdaDataset | BdaModel>(asset) && (
        <AssetItem
          asset={asset}
          type={type}
          actions={actions || <></>}
        />
      )}

      {type === "service" && setObjectType<BdaService>(asset) && (
        <ServiceItem service={asset} actions={actions || <></>} />
      )}
    </div>
  )
})
